import type {
  PublicMediaReference,
  PublicMediaRole,
  StorefrontContentMediaConfig,
} from '../../types/storefront-engine';

type UnknownRecord = Record<string, unknown>;

export const STOREFRONT_PUBLIC_MEDIA_BUCKET = 'storefront-media';
export const STOREFRONT_PUBLIC_MEDIA_MAX_BYTES = 5 * 1024 * 1024;
export const STOREFRONT_PUBLIC_MEDIA_MIN_DIMENSION_PX = 1;
export const STOREFRONT_PUBLIC_MEDIA_MAX_DIMENSION_PX = 4096;
export const STOREFRONT_PUBLIC_MEDIA_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export const STOREFRONT_PUBLIC_MEDIA_ROLES = [
  'section-image',
] as const satisfies readonly PublicMediaRole[];

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const PUBLIC_MEDIA_REFERENCE_KEYS = ['kind', 'mediaAssetId', 'role'] as const;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

export function isPublicMediaRole(value: unknown): value is PublicMediaRole {
  return (
    typeof value === 'string' &&
    (STOREFRONT_PUBLIC_MEDIA_ROLES as readonly string[]).includes(value)
  );
}

export function isPublicMediaReference(value: unknown): value is PublicMediaReference {
  return (
    isRecord(value) &&
    hasExactKeys(value, PUBLIC_MEDIA_REFERENCE_KEYS) &&
    value.kind === 'public-media-reference' &&
    typeof value.mediaAssetId === 'string' &&
    UUID_RE.test(value.mediaAssetId) &&
    isPublicMediaRole(value.role)
  );
}

export function sanitizePublicMediaReference(value: unknown): PublicMediaReference | null {
  return isPublicMediaReference(value)
    ? {
        kind: 'public-media-reference',
        mediaAssetId: value.mediaAssetId,
        role: value.role,
      }
    : null;
}

export type StorefrontSectionMediaSlot = 'desktop' | 'mobile';

export const STOREFRONT_SECTION_MEDIA_SLOTS = [
  'desktop',
  'mobile',
] as const satisfies readonly StorefrontSectionMediaSlot[];

const STOREFRONT_CONTENT_MEDIA_CONFIG_KEYS = STOREFRONT_SECTION_MEDIA_SLOTS;

/**
 * Key under `publicMediaBySectionId` for a per-slot section media resolution.
 * `.` is not a legal builder-generated section-id character (STOREFRONT_SECTION_ID_RE), so this
 * cannot collide with a builder-generated id. Mirrored verbatim in
 * `resolve_public_storefront_content_refs` (SQL) — change both or neither.
 */
export function storefrontSectionMediaSlotKey(
  sectionId: string,
  slot: StorefrontSectionMediaSlot,
): string {
  return `${sectionId}.${slot}`;
}

function sanitizeSectionImageReference(value: unknown): PublicMediaReference | null {
  const reference = sanitizePublicMediaReference(value);
  return reference && reference.role === 'section-image' ? reference : null;
}

/**
 * Shared responsive media group used by content sections that carry an image
 * (`contentSection.media`). Exact keys `desktop` + `mobile`; each slot is `null` or a
 * `section-image` public media reference. Anything else fails closed (`null`).
 */
export function sanitizeStorefrontContentMediaConfig(
  value: unknown,
): StorefrontContentMediaConfig | null {
  if (!isRecord(value) || !hasExactKeys(value, STOREFRONT_CONTENT_MEDIA_CONFIG_KEYS)) {
    return null;
  }

  const slots: Partial<Record<StorefrontSectionMediaSlot, PublicMediaReference | null>> = {};
  for (const slot of STOREFRONT_SECTION_MEDIA_SLOTS) {
    const raw = value[slot];
    if (raw === null) {
      slots[slot] = null;
      continue;
    }
    const reference = sanitizeSectionImageReference(raw);
    if (!reference) {
      return null;
    }
    slots[slot] = reference;
  }

  return { desktop: slots.desktop ?? null, mobile: slots.mobile ?? null };
}
